/**
 * Visualizes `CollisionZone`s — freestanding invisible collision volumes
 * (see types.ts). Shared between `ultra_map_editor` (always visible there,
 * it's an authoring tool) and the live game's debug overlay (hidden unless
 * DEBUG is toggled on) so both render the exact same box the collision math
 * in base-sim.ts actually uses — no separate "debug view" implementation
 * that could drift from what really blocks the player.
 */
import * as THREE from 'three';
import type { CollisionZone } from '../types';

const ZONE_DISPLAY_HEIGHT = 2.5;

export function buildZoneMesh(zone: CollisionZone): THREE.Object3D {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xff4444, transparent: true, opacity: 0.25, depthWrite: false,
  });
  // Unit-sized geometry, sized via `.scale` instead — so ultra_map_editor's
  // TransformControls scale mode can drive width/depth directly
  // (`mesh.scale.x`/`.z`) without rebuilding BoxGeometry on every drag
  // frame. `.scale.y` stays fixed at 1 (zones have a display-only height,
  // not a real map dimension).
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(1, ZONE_DISPLAY_HEIGHT, 1), mat);
  mesh.scale.set(zone.size.w, 1, zone.size.d);
  mesh.position.set(zone.position.x, ZONE_DISPLAY_HEIGHT / 2, zone.position.z);
  mesh.rotation.y = zone.rotationY ?? 0;
  const edges = new THREE.LineSegments(
    new THREE.EdgesGeometry(mesh.geometry),
    new THREE.LineBasicMaterial({ color: 0xff8888 }),
  );
  mesh.add(edges);
  return mesh;
}
